// CommunityBanner.tsx — detail-page gradient banner + identity + stats + actions.
import styled from 'styled-components';
import { Button } from '@/ui';
import { Icon } from '@/icons';
import { useCommunities, useRequireAuth } from '@/hooks';
import { media } from '@/theme/media';
import { CommunityIcon } from './CommunityIcon';
import { coverBackground } from './meta';
import { formatCount } from '@/components/formatCount';
import type { Community } from '@/core/models/community';
import { isHttpUrl } from '@/core/lib/isHttpUrl';

const Wrap = styled.div`
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  margin-bottom: 16px;
  ${media.mobile`border-radius: 0; border-left: none; border-right: none;`}
`;
const Cover = styled.div<{ $tone?: string; $image?: string }>`height: 120px; background: ${({ $tone, $image }) => $image ? `center / cover no-repeat url("${String($image).replace(/["\\]/g, '')}")` : coverBackground($tone)};`;
const Body = styled.div`padding: 0 18px 18px; background: ${({ theme }) => theme.color.glassBg}; backdrop-filter: blur(14px);`;
const IdRow = styled.div`display: flex; align-items: flex-end; gap: 14px; margin-top: -28px;`;
const IconHalo = styled.div`background: ${({ theme }) => theme.color.bgRoot}; padding: 4px; border-radius: ${({ theme }) => theme.radius.md};`;
const Identity = styled.div`flex: 1; min-width: 0; padding-bottom: 4px;`;
const NameRow = styled.div`display: flex; align-items: center; gap: 6px;`;
const Name = styled.h1`margin: 0; font-family: ${({ theme }) => theme.font.display}; font-size: ${({ theme }) => theme.fontSize.lg}; font-weight: 800; color: ${({ theme }) => theme.color.textPrimary};`;
const Tagline = styled.p`margin: 4px 0 0; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};`;
const Actions = styled.div`display: flex; gap: 8px; padding-bottom: 6px;`;
const Stats = styled.div`display: flex; gap: 20px; margin-top: 12px;`;
const Stat = styled.div`display: flex; flex-direction: column;`;
const StatNum = styled.span`font-family: ${({ theme }) => theme.font.display}; font-weight: 700; color: ${({ theme }) => theme.color.textPrimary};`;
const StatLabel = styled.span`font-family: ${({ theme }) => theme.font.mono}; font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted};`;

export interface CommunityBannerProps {
  community: Community;
  onCreatePost: () => void;
}

export function CommunityBanner({ community, onCreatePost }: CommunityBannerProps) {
  const { toggleJoin } = useCommunities();
  const requireAuth = useRequireAuth();
  const coverIsUrl = isHttpUrl(community.coverTone);
  function handleJoin() {
    if (!requireAuth(`join ${community.name}`)) return;
    void toggleJoin(community.id);
  }
  function handleCreate() {
    if (!requireAuth('post')) return;
    onCreatePost();
  }
  return (
    <Wrap>
      <Cover
        data-testid="community-cover"
        $tone={coverIsUrl ? undefined : community.coverTone}
        $image={coverIsUrl ? community.coverTone : undefined}
      />
      <Body>
        <IdRow>
          <IconHalo><CommunityIcon svg={community.icon} size={56} /></IconHalo>
          <Identity>
            <NameRow>
              <Name>{community.name}</Name>
              <Icon name="verify" size={18} title="Verified community" />
            </NameRow>
            <Tagline>{community.tagline}</Tagline>
          </Identity>
          <Actions>
            <Button variant={community.joined ? 'secondary' : 'outline'} onClick={handleJoin} aria-label={`${community.joined ? 'Leave' : 'Join'} ${community.name}`}>
              {community.joined ? 'Joined' : 'Join'}
            </Button>
            <Button variant="secondary" onClick={handleCreate}>Create Post</Button>
          </Actions>
        </IdRow>
        <Stats>
          <Stat><StatNum>{formatCount(community.membersNum)}</StatNum><StatLabel>Members</StatLabel></Stat>
          <Stat><StatNum>{community.postsCount}</StatNum><StatLabel>Posts</StatLabel></Stat>
          <Stat><StatNum>{formatCount(community.online)}</StatNum><StatLabel>Online</StatLabel></Stat>
        </Stats>
      </Body>
    </Wrap>
  );
}
